import { Directive, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { PlaybackService } from '../services/playback.service';
import { PlayerEngineService } from '../services/player-engine.service';
import { HuiPlaybackErrorTypes, HuiPlaybackStatusTypes, PSNotification } from '@hidat/huijs-player';
import { MediaToPlay } from '../models';
import { StationManagerService } from '../resources/station-manager.service';
import { EventTrackerService } from '../services/event-tracker.service';


/**
 * Player UI Control State
 * This represents the icon/control the UI should be showing, i.e.
 *   Indeterminate: We don't have anything to play, so don't show or disable player control
 *   Play: Show something to indicate that pressing it will START PLAYBACK
 *   Pause: Show something to indicate that pressing it will PAUSE PLAYBACK
 */
export enum PlayerControlState {
  Indeterminate,
  Play,
  Pause
}

/**
 * Player Controller Base
 * Inherit all your player UI components from this.
 */
@Directive()
export class PlayerController implements OnInit, OnDestroy {
  protected playbackService = inject(PlaybackService);
  protected playerEngine = inject(PlayerEngineService);
  protected stationManager = inject(StationManagerService);
  protected eventTracker = inject(EventTrackerService);
  protected eventSource = 'player';

  protected mediaPlaying: MediaToPlay | undefined;
  protected playing = signal(false);
  protected paused = signal(false);
  protected canPlay = signal(false); // Set to true once the current track is loaded.  Helps us track blocked content.
  protected percPlayed = signal(0);
  protected duration = signal(0);
  protected position = signal(0);
  protected playbackRate = signal(1);
  protected PlayerControlState = PlayerControlState; //Make available in HTML scope
  protected controlState = signal<PlayerControlState>(
    PlayerControlState.Indeterminate
  );
  protected mediaLoading = signal(false);
  protected mediaLoaded = signal(false);
  protected currentError: number | undefined;
  protected resetPlaybackPositionOnFinished = true;

  private playbackProgressSubscription$: Subscription | undefined;
  private playbackStatusSubscription$: Subscription | undefined;
  private playbackNotifierSubscription$: Subscription | undefined;

  ngOnInit(): void {
    this.setupSubscriptions();
    // Check initial state.  This covers instances where the player is created after the playback is started.
    this.setInitialState();
  }

  ngOnDestroy() {
    this.playbackStatusSubscription$?.unsubscribe();
    this.playbackProgressSubscription$?.unsubscribe();
    this.playbackNotifierSubscription$?.unsubscribe();
  }

  setupSubscriptions() {
    this.playbackStatusSubscription$ =
      this.playerEngine.playbackStatusSource.subscribe(
        (playbackStatusEvent) => {
          this.playing.set(playbackStatusEvent.playing);
          this.paused.set(playbackStatusEvent.paused);
          this.mediaPlaying = playbackStatusEvent.source as MediaToPlay;
          this.currentError =
            playbackStatusEvent.errorType || HuiPlaybackErrorTypes.NO_ERROR;
          switch (playbackStatusEvent.eventID) {
            case HuiPlaybackStatusTypes.LOADING:
              this.mediaLoading.set(true);
              this.mediaLoaded.set(false);
              this.controlState.set(PlayerControlState.Indeterminate);
              this.canPlay.set(false);
              break;

            case HuiPlaybackStatusTypes.STARTED:
              this.canPlay.set(true);
              if (playbackStatusEvent.reallyPlaying) {
                this.controlState.set(PlayerControlState.Pause);
              }
              break;

            case HuiPlaybackStatusTypes.REALLY_STARTED:
              this.canPlay.set(true);
              this.controlState.set(PlayerControlState.Pause);
              break;

            case HuiPlaybackStatusTypes.RESUMED:
              this.canPlay.set(true);
              this.controlState.set(PlayerControlState.Pause);
              break;

            case HuiPlaybackStatusTypes.PAUSED:
              this.canPlay.set(true);
              this.controlState.set(PlayerControlState.Play);
              break;

            case HuiPlaybackStatusTypes.FILE_LOADED:
              this.mediaLoading.set(false);
              this.mediaLoaded.set(true);
              this.canPlay.set(true);

              // if we aren't already playing, indicate that we can play
              if (!playbackStatusEvent.reallyPlaying) {
                this.controlState.set(PlayerControlState.Play);
              }
              this.onMediaLoaded();
              break;

            case HuiPlaybackStatusTypes.STOPPED:
              this.canPlay.set(true);
              this.controlState.set(PlayerControlState.Play);
              break;

            case HuiPlaybackStatusTypes.ERROR:
              this.canPlay.set(true);
              this.controlState.set(PlayerControlState.Indeterminate);
              break;

            case HuiPlaybackStatusTypes.FINISHED:
              this.controlState.set(PlayerControlState.Indeterminate);
              this.canPlay.set(false);
              if (this.resetPlaybackPositionOnFinished) {
                this.position.set(0);
                this.percPlayed.set(0);
                this.duration.set(0);
              }
              break;
          }
        }
      );

    this.playbackProgressSubscription$ =
      this.playerEngine.playbackProgressSource.subscribe((progressEvent) => {
        // If we are getting progress, then we are playing. Covers up many sins!
        this.playing.set(true);
        this.paused.set(false);

        this.percPlayed.set(progressEvent.percDone * 100);
        this.duration.set(progressEvent.duration);
        this.position.set(progressEvent.position);
        this.onPlaybackProgress(progressEvent.position);
      });

    this.playbackNotifierSubscription$ =
      this.playbackService.notifier.subscribe((notification) =>
        this.handlePlaybackNotification(notification)
      );
  }

  /**
   * Override in your component to handle out of content errors, etc...
   * @param notification
   * @protected
   */
  protected handlePlaybackNotification(notification: PSNotification) {}

  protected onPlaybackProgress(position: number) {}

  protected onMediaLoaded() {
    console.log('Media Loaded: ' + this.mediaPlaying?.uid);
  }

  protected setInitialState() {
    const status = this.playerEngine.getPlaybackStatus();
    this.mediaPlaying = status.source as MediaToPlay;
    if (status.playing) {
      this.canPlay.set(true);
      this.controlState.set(PlayerControlState.Pause);
    } else {
      this.canPlay.set(true);
      this.controlState.set(PlayerControlState.Play);
    }
  }

  public skipSeconds(skippingBack: boolean): number {
    let ms = this.stationManager.defaultSkipSeconds;
    const segment = this.mediaPlaying?._segment;
    if (segment) {
      if (segment.licensingKlass) {
        if (segment.licensingKlass.toLowerCase() === 'dmca') {
          if (skippingBack) {
            ms = -1;
          } else {
            ms = this.stationManager.dmcaSkipSeconds;
          }
        }
      }
    }
    return ms;
  }

  /**
   * Toggle pause.
   */
  togglePause() {
    if (this.canPlay()) {
      this.playbackService.togglePlayback(this.eventSource);
    }
  }

  /**
   * Skip ahead in the current stream
   */
  fastForward() {
    if (this.playing()) {
      const ms = this.skipSeconds(false);
      if (this.duration() - this.position() > ms) {
        this.playerEngine.setPosition((this.position() + ms) * 1000);
        this.eventTracker.playSkipForward(this.eventSource)
      }
    }
  }

  /**
   * Skip back in the current stream
   */
  reverse() {
    if (this.playing()) {
      let newPosition = 0;
      const ms = this.skipSeconds(true);
      if (this.position() > ms) {
        newPosition = (this.position() - ms) * 1000;
      }
      this.playerEngine.setPosition(newPosition);
      this.eventTracker.playSkipBackward(this.eventSource);
    }
  }

  togglePlaybackRate() {
    const cr = this.playbackRate();
    let nr = cr;
    if (cr < 4) {
      nr = 4;
    } else {
      nr = 1;
    }
    this.playbackRate.set(nr);
    this.playerEngine.setPlaybackRate(nr);
  }

  /**
   * Increase playback rate (chipmunks!)
   */
  fasterPlayback() {
    let nr = this.playbackRate();
    if (nr) {
      nr += 0.1;
      this.playerEngine.setPlaybackRate(nr);
      this.playbackRate.set(nr);
    }
  }

  /**
   * Decrease playback rate (Drunk Trump)
   */
  slowerPlayback() {
    let nr = this.playbackRate();
    if (nr > 0.5) {
      nr -= 0.1;
      this.playerEngine.setPlaybackRate(nr);
      this.playbackRate.set(nr);
    }
  }
}
